import axios from "axios";
import { bytesToGigabytes } from "../utils";

const ANALYTICS_NAME = "KvqPoHG2MH4YsbKpP468XGHHs";
const ANALYTICS_KEY = "7WmdkuQ8o4kL6X6rzCuqahmfT";

const CRUD_KEY = "WTw0fEUjXau3J5e79L6iycALE2456sRef9i0vw5kU";
const CRUD_NAME = "console";

const axiosWithAnalyticsGeneralUsageURL = () => {
  return axios.create({
    headers: {
      authorization: ANALYTICS_NAME,
      "wsc-api-key": ANALYTICS_KEY,
    },
    baseURL: "https://playerstats-v2.invintus.com/generalusage",
  });
};

const axiosWithAnalyticsStatsURL = () => {
  return axios.create({
    headers: {
      authorization: ANALYTICS_NAME,
      "wsc-api-key": ANALYTICS_KEY,
    },
    baseURL: "https://playerstats-v2.invintus.com/stats",
  });
};

const axiosWithCrudUrl = () => {
  return axios.create({
    headers: {
      authorization: CRUD_NAME,
      "wsc-api-key": CRUD_KEY,
    },
    baseURL: "https://crud.v3.invintusmedia.com",
  });
};

// get number of sources
export const getNumberOfSources = async (clientID) => {
  const { data } = await axiosWithAnalyticsGeneralUsageURL().post(
    "/numberOfSources",
    { clientID }
  );

  const { result } = data;

  return result;
};

// get total events scheduled for the "Events Scheduled"
export const getTotalEventsScheduled = async (clientID) => {
  const { data } = await axiosWithAnalyticsGeneralUsageURL().post(
    "/totalEventsScheduled",
    { clientID }
  );

  const { result } = data;

  return result.totalEvents;
};

// get total live events for the events broadcasted graph
const defaultWeek = [
  {
    dateLabel: "Mon",
    "Number of Events": 0,
  },
  {
    dateLabel: "Tue",
    "Number of Events": 0,
  },
  {
    dateLabel: "Wed",
    "Number of Events": 0,
  },
  {
    dateLabel: "Thu",
    "Number of Events": 0,
  },
  {
    dateLabel: "Fri",
    "Number of Events": 0,
  },
  {
    dateLabel: "Sat",
    "Number of Events": 0,
  },
  {
    dateLabel: "Sun",
    "Number of Events": 0,
  },
];

const defaultMonth = [
  {
    dateLabel: "Week 1",
    "Number of Events": 0,
  },
  {
    dateLabel: "Week 2",
    "Number of Events": 0,
  },
  {
    dateLabel: "Week 3",
    "Number of Events": 0,
  },
  {
    dateLabel: "Week 4",
    "Number of Events": 0,
  },
];

const defaultYear = [
  {
    dateLabel: "Jan",
    "Number of Events": 0,
  },
  {
    dateLabel: "Feb",
    "Number of Events": 0,
  },
  {
    dateLabel: "Mar",
    "Number of Events": 0,
  },
  {
    dateLabel: "Apr",
    "Number of Events": 0,
  },
  {
    dateLabel: "May",
    "Number of Events": 0,
  },
  {
    dateLabel: "June",
    "Number of Events": 0,
  },
  {
    dateLabel: "July",
    "Number of Events": 0,
  },
  {
    dateLabel: "Aug",
    "Number of Events": 0,
  },
  {
    dateLabel: "Sep",
    "Number of Events": 0,
  },
  {
    dateLabel: "Oct",
    "Number of Events": 0,
  },
  {
    dateLabel: "Nov",
    "Number of Events": 0,
  },
  {
    dateLabel: "Dec",
    "Number of Events": 0,
  },
];

export const getTotalLiveEventsByRange = async ({ clientID, range }) => {
  const { data } = await axiosWithAnalyticsGeneralUsageURL().post(
    "/totalLiveEventsByRange",
    { clientID, range }
  );

  const { result: events } = data;

  if (events.length === 0) {
    const defaults = {
      week: defaultWeek,
      month: defaultMonth,
      year: defaultYear,
    };

    return defaults[range];
  }

  if (range === "week") {
    const weekMap = {
      Mon: 0,
      Tue: 0,
      Wed: 0,
      Thu: 0,
      Fri: 0,
      Sat: 0,
      Sun: 0,
    };

    events.forEach((event) => {
      event.day = new Date(event.day).toLocaleString("en-us", {
        weekday: "short",
      });

      weekMap[event.day] += Number(event.totEvents);
    });

    let result = [];
    for (const [key, val] of Object.entries(weekMap)) {
      result.push({ dateLabel: key, "Number of Events": val });
    }

    return result;
  }

  if (range === "month") {
    const uniqueWeek = {};

    for (const event of events) {
      const weekOfMonth = Math.ceil(new Date(event.day).getDate() / 7);

      if (uniqueWeek[weekOfMonth])
        uniqueWeek[weekOfMonth] += Number(event.totEvents);
      else uniqueWeek[weekOfMonth] = Number(event.totEvents);
    }

    let result = [];

    const weeklyMonthBreakUp = ["Week 1", "Week 2", "Week 3", "Week 4"];
    const correspondingWeeklyEvents = Object.values(uniqueWeek);

    weeklyMonthBreakUp.forEach((weekLabel, i) => {
      let eventsThatWeek = correspondingWeeklyEvents[i];

      if (typeof eventsThatWeek === "undefined") eventsThatWeek = 0;

      result.push({
        dateLabel: `${weekLabel}`,
        "Number of Events": eventsThatWeek,
      });
    });

    return result;
  }

  if (range === "year") {
    let result = [];
    const monthIndexMap = {
      1: "Jan",
      2: "Feb",
      3: "Mar",
      4: "Apr",
      5: "May",
      6: "Jun",
      7: "Jul",
      8: "Aug",
      9: "Sep",
      10: "Oct",
      11: "Nov",
      12: "Dec",
    };

    for (const event of events) {
      const monthIndex = Number(event.month.split("-")[1]);

      result.push({
        dateLabel: monthIndexMap[monthIndex],
        "Number of Events": Number(event.totEvents),
      });
    }
    return result;
  }
};

// get viewing hours by range, "Live Viewing Hours", "Vod Viewing Hours"
export const getLiveAndVodHours = async ({ clientID, startDate, endDate }) => {
  const { data } = await axiosWithAnalyticsGeneralUsageURL().post(
    "/totalProducedHoursByRange",
    { clientID, startDate, endDate }
  );

  const { result } = data;

  const live =
    result.liveHours.length > 0
      ? result.liveHours.reduce((acc, cur) => (acc += +cur.hours), 0)
      : 0;

  const vod =
    result.vodHours.length > 0
      ? result.vodHours.reduce((acc, cur) => (acc += +cur.hours), 0)
      : 0;

  return {
    live,
    vod,
    combined: live + vod,
  };
};

// get users for the doughnut users chart
export const getUsers = async ({ clientID }) => {
  const { data } = await axiosWithCrudUrl().post("/Users/getUsers", {
    clientID,
  });

  const rawUsers = data.data;

  const userCounts = {
    Administrator: rawUsers.reduce(
      (count, user) => (user.userRole === "1" ? count + 1 : count),
      0
    ),
    "Power User": rawUsers.reduce(
      (count, user) => (user.userRole === "2" ? count + 1 : count),
      0
    ),
    Technician: rawUsers.reduce(
      (count, user) => (user.userRole === "3" ? count + 1 : count),
      0
    ),
    "Read Only": rawUsers.reduce(
      (count, user) => (user.userRole === "4" ? count + 1 : count),
      0
    ),
    Analyst: rawUsers.reduce(
      (count, user) => (user.userRole === "5" ? count + 1 : count),
      0
    ),
  };

  const usersInfo = {
    total: rawUsers.length,
    userCategories: Object.entries(userCounts).map((user, i) => {
      return {
        typeOfUser: user[0],
        count: user[1],
      };
    }),
  };

  return usersInfo;
};

// get most watched events by range for the "Most Watched" carousel
export const getMostWatchedEventsByRange = async ({
  clientID,
  startDate,
  endDate,
}) => {
  const { data } = await axiosWithAnalyticsGeneralUsageURL().post(
    `/topEventPlaysByRange`,
    { clientID, startDate, endDate }
  );

  const { result } = data;

  if (!result) return []; // handles the case where there are no events and null is returned

  return result;
};

// get billing usage by range
export const getBillingUsageByRange = async ({
  clientID,
  startDate,
  endDate,
}) => {
  const { data } = await axiosWithAnalyticsStatsURL().post(`/getBillingUsage`, {
    clientID,
    startDate,
    endDate,
  });

  const { result } = data;

  if (!result) return {};

  return result[0];
};

export const getTopEventConsumersByRange = async ({
  clientID,
  startDate,
  endDate,
}) => {
  const { data } = await axiosWithAnalyticsGeneralUsageURL().post(
    `/topEventConsumersByRange`,
    {
      clientID,
      startDate,
      endDate,
    }
  );

  const { result } = data;

  return result
    .map((event) => {
      const eventID = event.eventID.split("_")[1];
      const liveGigs = bytesToGigabytes(event.liveBytes).toFixed(2);
      const vodGigs = bytesToGigabytes(event.vodBytes).toFixed(2);
      const totalGigsManuallyAdded = (
        Number(liveGigs) + Number(vodGigs)
      ).toFixed(2);

      return {
        eventID,
        shortTitle: event.title.slice(0, 25) + "...",
        title: event.title,
        Live: liveGigs,
        Vod: vodGigs,
        "Total Consumption": totalGigsManuallyAdded,
      };
    })
    .sort(({ "Total Consumption": a }, { "Total Consumption": b }) => b - a);
};

export const getEventConsumptionByEventIDAndRange = async ({
  clientID,
  startDate,
  endDate,
  eventID,
}) => {
  const { data } = await axiosWithAnalyticsGeneralUsageURL().post(
    `/eventConsumptionByRange`,
    {
      clientID,
      startDate,
      endDate,
      eventID,
    }
  );

  const { result } = data;

  if (result.length === 1) {
    const event = result[0];
    const liveGigs = bytesToGigabytes(event.liveBytes).toFixed(2);
    const vodGigs = bytesToGigabytes(event.vodBytes).toFixed(2);

    const [year, month, day] = event.logDay.split("-");

    return [
      {
        "Live Consumption": liveGigs,
        "Vod Consumption": vodGigs,
        dateLabel: `${month}/${day}`,
        logDay: `${month}/${day}/${year}`,
      },
      {
        "Live Consumption": liveGigs,
        "Vod Consumption": vodGigs,
        dateLabel: `${month}/${day}`,
        logDay: `${month}/${day}/${year}`,
      },
    ];
  }

  return result.map((event) => {
    const liveGigs = bytesToGigabytes(event.liveBytes).toFixed(2);
    const vodGigs = bytesToGigabytes(event.vodBytes).toFixed(2);

    const [year, month, day] = event.logDay.split("-");

    return {
      "Live Consumption": liveGigs,
      "Vod Consumption": vodGigs,
      dateLabel: `${month}/${day}`,
      logDay: `${month}/${day}/${year}`,
    };
  });
};
